<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { formatShortDate } from '@/lib/utils'

import { useAccountingStore } from '@/stores/accounting.store'
import { infoBoxToneEnum } from '@/stores/portal.store'

import { useNavigator } from '@/composables/navigator'
import { formatExpenseMoney } from '@/composables/expense-claim'

import DescriptionSection from '@/components/shared/DescriptionSection.vue'
import InfoBox from '@/components/shared/InfoBox.vue'
import NavigateBack from '@/components/shared/NavigateBack.vue'
import PdfPreviewPage from '@/components/shared/PdfPreviewPage.vue'

const props = defineProps({
  rows: {
    type: Array,
    required: true,
  },
  exclusions: {
    type: Object,
    default: () => ({}),
  },
  canExclude: {
    type: Boolean,
    default: false,
  },
})
const index = defineModel('index', { type: Number, required: true })

const emit = defineEmits(['cancel', 'toggle-exclusion'])

const IMAGE_FILE_PATTERN = /\.(png|jpe?g|gif|webp|bmp)$/i
const PDF_FILE_PATTERN = /\.pdf$/i

const i18n = getI18n()
const accountingStore = useAccountingStore()

const slide = ref(0)
const fileUrls = ref({})
const pdfFile = ref(null)

useNavigator(() => emit('cancel'))

const row = computed(() => props.rows[index.value])
const isLast = computed(() => index.value === props.rows.length - 1)
const tagName = computed(() => accountingStore.costReportRowTagById.get(row.value.tag_id ?? row.value.tag)?.name ?? i18n.t('common.expense'))
const pendingExclusion = computed(() => props.exclusions[row.value.id])
const exclusionReason = computed(() => row.value.exclusion_reason || pendingExclusion.value)

const isImage = file => IMAGE_FILE_PATTERN.test(file.name)
const isPdf = file => PDF_FILE_PATTERN.test(file.name)

const loadFilesAsync = (targetRow) => Promise.all((targetRow.files ?? [])
  .filter(file => !fileUrls.value[file.id])
  .map(async(file) => {
    const url = await accountingStore.getCostReportRowFileUrlAsync(file.id)
    fileUrls.value = { ...fileUrls.value, [file.id]: url }
  }))

const goTo = (target) => {
  index.value = target
  slide.value = 0
  loadFilesAsync(props.rows[target])
}

const onDownloadAsync = (file) => accountingStore.downloadCostReportRowFileAsync(file.id, file.name)
const openFile = (file) => {
  if (isPdf(file)) pdfFile.value = file
  else onDownloadAsync(file)
}

onMounted(() => loadFilesAsync(row.value))
onBeforeUnmount(() => Object.values(fileUrls.value).forEach(url => URL.revokeObjectURL(url)))
</script>

<template>
  <q-card class="dialog-card-flex">
    <q-dialog :model-value="pdfFile !== null" maximized @hide="pdfFile = null">
      <pdf-preview-page
        v-if="pdfFile !== null"
        :title="pdfFile.name"
        :source="fileUrls[pdfFile.id]"
        :loading="!fileUrls[pdfFile.id]"
        @cancel="pdfFile = null"
      />
    </q-dialog>

    <navigate-back :text="tagName" @cancel="emit('cancel')">
      <template #right>
        <div class="row no-wrap items-center text-caption text-grey-7">
          <q-btn
            flat
            round
            dense
            icon="mdi-chevron-left"
            :disable="index === 0"
            @click="goTo(index - 1)"
          />
          <span>{{ index + 1 }}/{{ props.rows.length }}</span>
          <q-btn
            flat
            round
            dense
            icon="mdi-chevron-right"
            :disable="isLast"
            @click="goTo(index + 1)"
          />
        </div>
      </template>
    </navigate-back>

    <q-card-section class="scroll col q-pa-none" :class="{ 'dialog-card-content': props.canExclude }">
      <q-carousel
        v-if="row.files?.length"
        v-model="slide"
        class="receipt-viewer bg-grey-9"
        swipeable
        animated
        control-color="white"
        :arrows="row.files.length > 1"
        :navigation="row.files.length > 1"
      >
        <q-carousel-slide
          v-for="(file, position) in row.files"
          :key="file.id"
          :name="position"
          class="column flex-center q-pa-none"
        >
          <q-img
            v-if="isImage(file) && fileUrls[file.id]"
            :src="fileUrls[file.id]"
            fit="contain"
            class="full-width full-height"
          />
          <q-spinner v-else-if="isImage(file)" color="white" size="md" />
          <q-btn
            v-else
            flat
            no-caps
            stack
            color="white"
            :icon="isPdf(file) ? 'mdi-file-pdf-box' : 'mdi-file-outline'"
            :label="file.name"
            @click="openFile(file)"
          />
          <q-btn
            class="absolute-top-right q-ma-sm"
            round
            dense
            color="white"
            text-color="dark"
            icon="mdi-download"
            @click="onDownloadAsync(file)"
          >
            <q-tooltip>{{ i18n.t('common.download') }}</q-tooltip>
          </q-btn>
        </q-carousel-slide>
      </q-carousel>
      <div v-else class="receipt-viewer bg-grey-9 text-white column flex-center q-gutter-sm">
        <q-icon name="mdi-paperclip-off" size="lg" />
        <span class="text-caption">{{ i18n.t('common.noReceiptAttached') }}</span>
      </div>

      <div class="q-px-sm q-pt-sm">
        <info-box
          v-if="exclusionReason"
          class="q-mb-sm"
          :tone="infoBoxToneEnum.WARNING"
          :message="i18n.t('common.expenseClaimExcludedBecause', { reason: exclusionReason })"
        />
      </div>

      <q-list>
        <q-item>
          <div style="width: 50%; float: left;">
            <q-item-section>
              <q-item-label caption>{{ i18n.t('common.date') }}</q-item-label>
              <q-item-label>{{ formatShortDate(row.date) }}</q-item-label>
            </q-item-section>
          </div>
          <div style="width: 50%; float: right;">
            <q-item-section class="text-right">
              <q-item-label caption>{{ i18n.t('common.amount') }}</q-item-label>
              <q-item-label class="text-weight-bold" :class="{ 'text-strike text-grey-6': exclusionReason }">{{ formatExpenseMoney(row.amount_reference) }}</q-item-label>
            </q-item-section>
          </div>
        </q-item>
        <q-item>
          <div style="width: 50%; float: left;">
            <q-item-section v-if="row.quantity != null">
              <q-item-label caption>{{ i18n.t('common.quantity') }}</q-item-label>
              <q-item-label>{{ Number(row.quantity) }}</q-item-label>
            </q-item-section>
          </div>
          <div style="width: 50%; float: right;">
            <q-item-section class="text-right">
              <q-item-label caption>{{ i18n.t('common.project') }}</q-item-label>
              <q-item-label>{{ row.project_name ?? i18n.t('common.generalExpenses') }}</q-item-label>
            </q-item-section>
          </div>
        </q-item>
        <description-section v-if="row.notes" :text="row.notes" :label="i18n.t('common.notes')" />
      </q-list>
    </q-card-section>

    <q-card-actions v-if="props.canExclude" class="align-center dialog-card-bottom-bar q-gutter-sm">
      <q-btn
        flat
        color="warning"
        :icon="pendingExclusion !== undefined ? 'mdi-undo' : 'mdi-close-circle-outline'"
        :label="pendingExclusion !== undefined ? i18n.t('common.restore') : i18n.t('common.expenseClaimExcludeRow')"
        @click="emit('toggle-exclusion', row)"
      />
      <q-btn
        v-if="!isLast"
        color="primary"
        icon-right="mdi-chevron-right"
        :label="i18n.t('common.next')"
        @click="goTo(index + 1)"
      />
    </q-card-actions>
  </q-card>
</template>

<style scoped>
.receipt-viewer {
  height: 45vh;
}
</style>
